import { observer } from 'mobx-react';
import type { ReactNode } from 'react';
import { PaymentFunnel } from '../components/profile-payment/payment-funnel';
import { ProfilePaymentCard } from '../components/profile-payment/profile-payment-card';
import { PageMeta } from '../contentful-components/page-meta';
import { usePaymentFormPage } from '../graphql/hooks/form-page';
import { usePage } from '../graphql/hooks/page';
import { AssetTag, useRandomVideos } from '../graphql/hooks/video';
import { useIntentStage } from '../lib/store/p2p/context';
import { PaymentIntentStage } from '../lib/store/p2p/PaymentIntentStage';
import { ProfilePaymentProvider, useProfilePaymentStore } from '../lib/store/profile-payment/profile-pay-context';
import type { PageWithContext } from '../lib/utils/context';
import type { PageOptions } from '../lib/utils/page';
import { PAGE_OPTIONS } from '../lib/utils/page';
import type { ProfilePaymentPageProps } from '../server/helpers/profile';

/**
 * Persistent PaymentLayout wrapper for personalized payment flow.
 * Contents will be rendered inside the payment funnel layout.
 */
export const getProfilePaymentLayout = (config: Props) => (page: ReactNode, props: ProfilePaymentPageProps) => {
  const {
    profile
  } = props;
  if (profile?.hasPublicPayLink === false) return null;
  return <ProfilePaymentProvider {...props}>
                <PaymentWrapper {...props} {...config}>
                    {page}
                </PaymentWrapper>
            </ProfilePaymentProvider>;
};
const {
  REQUEST_MONEY
} = PAGE_OPTIONS;
const PaymentWrapper: PageWithContext<Props & ProfilePaymentPageProps> = observer(props => {
  const {
    children,
    meta: metaProps,
    profile,
    slug
  } = props;
  const store = useProfilePaymentStore();
  const stage = useIntentStage();
  const {
    videos
  } = useRandomVideos([AssetTag.PayMePage]);
  const startOfFunnel = [undefined, PaymentIntentStage.Input].includes(stage);
  const {
    page: form
  } = usePaymentFormPage({
    stage: stage,
    enabled: !!stage
  });
  const {
    page: regular
  } = usePage({
    slug: REQUEST_MONEY.slug,
    includeBanner: startOfFunnel,
    enabled: !stage,
    personalizations: store?.personalizations
  });
  const page = stage ? form : regular;
  const {
    meta
  } = page;
  return <>
                {meta && <PageMeta {...metaProps} id={meta.sys.id} name={metaProps?.name ?? slug} />}

                {startOfFunnel && <ProfilePaymentCard profile={profile} videos={videos} />}

                {stage && <PaymentFunnel>{children}</PaymentFunnel>}
            </>;
});
type Props = PageOptions<'REQUEST_MONEY' | 'PAYING' | 'PAYMENT_FAIL' | 'CONFIRM_PAYMENT' | 'PAYMENT_SUCCESS' | 'PAY_WITH_CARD'> & {
  /**
   * Page to render.
   */
  children?: ReactNode;
};